import React, { useEffect, useLayoutEffect, useRef } from 'react'
import { flushSync } from 'react-dom'
import Box from '@mui/material/Box'
import SearchIcon from '@mui/icons-material/Search'
import HelpOutlineIcon from '@mui/icons-material/HelpOutline'
import { useShallow } from 'zustand/react/shallow'
import { usePipelineStore } from '../stores/pipelineStore'
import { useOpenPerson } from '../hooks/useOpenPerson'
import { useOpportunityDeepLink } from '../hooks/useOpportunityDeepLink'
import { usePageTour } from '../hooks/usePageTour'
import { CrmTopbarPortal, useCrmOwner } from '../CrmLayout'
import { PipelineColumn } from './PipelineColumn'
import { ownerColor } from './OpportunityCard'
import './PipelinePage.scss'

// Matches PeoplePage: a search term is sent once it settles, not per keystroke.
const SEARCH_DEBOUNCE_MS = 300

// The Pipeline tour (Driver.js). Bump the version only for a change worth
// showing everyone again: progress saved under another version reads as new.
const TOUR_VERSION = 1

// The New Lead form, highlighted from the new-lead step: the modal isn't open
// when the tour picks its steps, so it can't be one. It shares new-lead's id,
// so it saves nothing new and a resume starts back on the button.
const NEW_LEAD_FORM = {
    element: '.nl-modal',
    data: { id: 'new-lead' },
    // Runs on every way off this step, closing included. Scoped: the attach
    // modal has an `.nl-x` too.
    onDeselected: () => document.querySelector('.nl-modal .nl-x')?.click(),
    popover: {
        title: 'New Lead form',
        description: 'Add the person’s details and choose where they belong in your pipeline. You can also select an existing person to avoid creating a duplicate. Nothing is saved until you select Create Lead.',
        // highlight() shows no buttons unless told to.
        showButtons: ['next', 'previous', 'close'],
        onNextClick: (el, step, { driver }) => driver.moveNext(),
        onPrevClick: (el, step, { driver }) => driver.moveTo(driver.getActiveIndex()),
    },
}

// Canonical order. Progress is saved as a step's id, never its index: a step
// whose target isn't on screen (no cards, not an admin) is left out of a run.
const TOUR_STEPS = [
    {
        element: '#PipelinePage .col',
        data: { id: 'pipeline' },
        popover: {
            title: 'Your pipeline',
            description: 'Your leads are organized into stages so you can quickly see where each opportunity stands. These stages can be customized by your account admin.',
        },
    },
    {
        element: '#PipelinePage [data-opportunity-id]',
        data: { id: 'lead-card' },
        popover: {
            title: 'Lead cards',
            description: 'Each card shows the key details for a lead. Open a card when you want to view that person’s details.',
        },
    },
    {
        element: '#PipelinePage .board-wrap',
        data: { id: 'move-leads' },
        popover: {
            title: 'Move leads forward',
            description: 'Drag leads between stages or reorder them as they progress. On mobile, use the Move to option instead.',
        },
    },
    {
        element: '.crm-topbar .crm-new-btn',
        data: { id: 'new-lead' },
        popover: {
            title: 'Add a lead',
            description: 'Use New Lead when you want to add someone directly to the pipeline.',
            // Opens the real modal through CrmLayout's own button, then shows it.
            onNextClick: (el, step, { driver }) => {
                flushSync(() => document.querySelector('.crm-topbar .crm-new-btn')?.click())
                if (document.querySelector('.nl-modal')) driver.highlight(NEW_LEAD_FORM)
                else driver.moveNext()
            },
        },
    },
    {
        // In the tab bar on desktop, the page's own toolbar on phones.
        element: '.crm-page-actions .search-box, #PipelinePage .search-box',
        data: { id: 'search' },
        popover: {
            title: 'Find a lead',
            description: 'Use search to quickly find a customer or vehicle in your pipeline.',
        },
    },
    {
        element: '.crm-view-select',
        data: { id: 'user-filter' },
        popover: {
            title: 'Choose whose leads to view',
            description: 'View everyone’s pipeline together or focus on a specific team member.',
        },
    },
    {
        element: '.crm-tour-btn',
        data: { id: 'replay' },
        popover: {
            title: 'Replay any time',
            description: 'You can start this tutorial again anytime from here.',
        },
    },
]

// `?opportunity=<id>` — the deep link a notification uses.
//
// A component rather than an inline call so a picker can leave it out without
// a conditional hook call, and so the hook's unmount cleanup stays tied to the
// real board — closing a picker must not wipe a highlight the user was sent to.
const DeepLinkWatcher = () => {
    useOpportunityDeepLink()
    return null
}

// `selection` turns the board into a picker for AttachToLeadsModal:
// { selectedIds, onToggle }. Without it — how the CRM route always renders it —
// every branch below takes its original path.
export const PipelinePage = ({ selection }) => {
    const {
        stages, loading, counts, fetchPipeline, applyFilters, moveOpportunity,
        loadMoreForStage, search, repFilter, setSearch, setRepFilter,
    } = usePipelineStore(
        useShallow((state) => ({
            stages: state.stages,
            loading: state.loading,
            counts: state.counts,
            fetchPipeline: state.fetchPipeline,
            applyFilters: state.applyFilters,
            moveOpportunity: state.moveOpportunity,
            loadMoreForStage: state.loadMoreForStage,
            search: state.search,
            repFilter: state.repFilter,
            setSearch: state.setSearch,
            setRepFilter: state.setRepFilter,
        }))
    )

    // Separate subscription so cards re-render when a column loads or a card
    // moves between columns.
    const byStage = usePipelineStore((state) => state.byStage)
    const focusedOpportunityId = usePipelineStore((state) => state.focusedOpportunityId)
    const setFocusedOpportunity = usePipelineStore((state) => state.setFocusedOpportunity)
    const openPerson = useOpenPerson()
    const boardRef = useRef(null)
    const tour = usePageTour({ key: 'pipeline', version: TOUR_VERSION, steps: TOUR_STEPS })

    // A picker is a second, throwaway board. Everything the CRM route's board
    // owns app-wide — `?opportunity=`, the tab bar's controls, the remembered
    // scroll offset, the spotlight, and any mutation — stays with the real one;
    // that single rule is what every `selecting` below is enforcing.
    const selecting = Boolean(selection)
    const spotlightId = selecting ? null : focusedOpportunityId

    const openOpportunity = (opp) => {
        const personId = opp.person_detail?.id || opp.person
        if (personId) openPerson(personId)
    }

    // Rep chips: the owners with open pursuits, with counts, from the aggregate
    // endpoint. They used to be derived from the loaded cards, which only worked
    // while the board held every one of them — with a column loaded a page at a
    // time a rep could vanish from their own filter strip. The list deliberately
    // ignores the active rep filter (see views.counts), so selecting a chip
    // can't collapse the strip to one entry and strand the user inside it.
    // Picker only: on the CRM route the topbar's view selector is the filter.
    const reps = counts.owners
    const showRepStrip = selecting && reps.length > 1

    // Page 1 for the topbar view's owner, on mount and whenever it changes. A
    // picker opens on Everyone, whatever the CRM view; its own chips narrow it,
    // and AttachToLeadsModal puts the board's owner back on close.
    const owner = useCrmOwner()
    useEffect(function fetchPipelineForView() {
        fetchPipeline(selecting ? 'all' : owner)
    }, [owner])

    // A new term is a new result set: every column restarts at page 1 with
    // `?search=` on the request, so a match on a page nobody scrolled to is
    // still found. Skipped on the first run — the mount effect above has just
    // fetched, and it already reads whatever search survived the last visit.
    const firstRun = useRef(true)
    useEffect(function refetchOnSearch() {
        if (firstRun.current) {
            firstRun.current = false
            return undefined
        }
        const timer = setTimeout(() => applyFilters(), SEARCH_DEBOUNCE_MS)
        return () => clearTimeout(timer)
    }, [search])

    // Coming back from a person's detail remounts the board, so put the user
    // back where they were. Only the horizontal offset is meaningful — the
    // board scrolls sideways and each column owns its own vertical scroll — so
    // this is unaffected by how many pages a column has loaded.
    //
    // Effectively mount-only: `selecting` never changes for a mounted board and
    // is in the deps only to keep the linter honest.
    useLayoutEffect(function restoreBoardScroll() {
        if (selecting) return
        const el = boardRef.current
        if (!el) return
        const { left, top } = usePipelineStore.getState().scroll
        el.scrollLeft = left
        el.scrollTop = top
    }, [selecting])

    const rememberBoardScroll = (e) => {
        usePipelineStore.setState({
            scroll: { left: e.currentTarget.scrollLeft, top: e.currentTarget.scrollTop },
        })
    }

    // The spotlight gets out of the way the moment the user engages with the
    // board — it has done its job by then, and leaving half the pipeline greyed
    // out while someone works would be worse than never highlighting at all.
    // Inert on a picker, where `spotlightId` is always null.
    const clearSpotlight = () => spotlightId && setFocusedOpportunity(null)

    // The first-visit tour, over a loaded board. `loading` is read live — a
    // warm store's value at mount predates the fetch the mount effect above
    // has just started.
    useEffect(function autoStartTour() {
        if (selecting) return
        if (usePipelineStore.getState().loading || stages.length === 0) return
        tour.autoStart()
    }, [loading])

    // Count and search sit in the CRM tab bar, so the board has no title row.
    // On phones they keep their own row.
    const activeBadge = <span className="badge">{counts.total} active</span>
    const searchBox = (
        <div className="search-box">
            <SearchIcon />
            <input
                placeholder="Search leads…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
            />
        </div>
    )
    const tourButton = (
        <button
            type="button"
            className="crm-tour-btn"
            aria-label="Show Tutorial"
            title="Show Tutorial"
            onClick={tour.replay}
        >
            <HelpOutlineIcon />
            <span className="crm-tour-label">Show Tutorial</span>
        </button>
    )

    return (
        <Box id="PipelinePage" className="pipeline-page">
            {!selecting && <DeepLinkWatcher />}

            {/* A picker brings its own header and search. */}
            {!selecting && (
                <CrmTopbarPortal
                    fallback={(
                        <div className="toolbar">
                            <span className="toolbar-title">Pipeline</span>
                            {activeBadge}
                            <div className="toolbar-divider" />
                            {searchBox}
                            {tourButton}
                        </div>
                    )}
                >
                    {activeBadge}
                    {searchBox}
                    {tourButton}
                </CrmTopbarPortal>
            )}

            {showRepStrip && (
                <div className="filterstrip">
                    <span className="fs-label">Showing</span>
                    <span
                        className={`rep-chip all${repFilter === 'all' ? ' active' : ''}`}
                        onClick={() => setRepFilter('all')}
                    >
                        <span className="rep-dot" style={{ background: '#7048cc' }}>★</span>
                        {/* The roster's sum, not `total`, which follows the picked rep. */}
                        Everyone <span className="rep-count">{reps.reduce((n, r) => n + r.count, 0)}</span>
                    </span>
                    {reps.map((r) => (
                        <span
                            key={r.owner_id}
                            className={`rep-chip${repFilter === r.owner_id ? ' active' : ''}`}
                            onClick={() => setRepFilter(r.owner_id)}
                        >
                            <span className="rep-dot" style={{ background: ownerColor(r.owner_id) }}>
                                {(r.name || '?').slice(0, 2).toUpperCase()}
                            </span>
                            {r.name} <span className="rep-count">{r.count}</span>
                        </span>
                    ))}
                </div>
            )}

            {loading && stages.length === 0 ? (
                <div className="pipeline-loading">Loading pipeline…</div>
            ) : stages.length === 0 ? (
                <div className="pipeline-loading">No pipeline stages yet.</div>
            ) : (
                <div
                    className="board-wrap"
                    ref={boardRef}
                    onScroll={selecting ? undefined : rememberBoardScroll}
                    onClick={clearSpotlight}
                >
                    <div className={`board${spotlightId ? ' has-focus' : ''}`}>
                        {/* Picking leads must neither move a pursuit nor navigate
                            off the quote, so the picker simply isn't given the
                            handlers that would. */}
                        {stages.map((stage) => (
                            <PipelineColumn
                                key={stage.id}
                                stage={stage}
                                column={byStage[String(stage.id)]}
                                count={counts.stages[String(stage.id)] || 0}
                                allStages={stages}
                                onDropOpportunity={selecting ? undefined : moveOpportunity}
                                onOpenOpportunity={selecting ? undefined : openOpportunity}
                                onLoadMore={loadMoreForStage}
                                selection={selection}
                            />
                        ))}
                    </div>
                </div>
            )}
        </Box>
    )
}
